import PageCard from "features/shared/components/card/PageCard";
import React, { useEffect, useState } from "react";
import {
  Customer_ContractDropdown,
  Contract_Readings_LastKnown,
  InvoiceSimulations_Calculate,
  Contract_InvoiceSimulations,
} from "data/endpoints";
import DataQuery from "data/DataQuery";
import ContractSelect from "features/contracts/components/ContractSelect";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import { useSelector } from "react-redux";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { ContractType } from "features/contracts/enums/contractType";
import { ContractStatus } from "features/contracts/enums/contractStatus";
import { useQuery } from "data/hooks/useQuery";
import ContractDropdownDTO from "features/contracts/dtos/ContractDropdownDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import { newSimulationFormValidations } from "./newSimulationFormValidations";
import { useForm } from "features/shared/forms/hooks/useForm";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import LastKnownReadingDTO from "features/contracts/dtos/LastKnownReadingDTO";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import ContentDialogAlert from "features/shared/components/contentDialog/ContentDialogAlert";
import IRepository from "data/IRepository";
import DataGrid from "features/shared/components/dataGrid/DataGrid";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import InvoiceSimulation from "../components/InvoiceSimulation";
import InvoiceSimulationHeader from "../components/InvoiceSimulationHeader";
import InvoiceSimulationSectionHandler from "./InvoiceSimulationSectionHandler";
import { useHistory } from "react-router-dom";
import CalculateResponseDTO from "../dto/CalculateResponseDTO";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import { useTranslation } from "react-i18next";
import Alert from "features/shared/alerts/components/Alert";

const invoiceSimulationDataFilterJson = loadDataFilterConfig("invoiceSimulationDataFilter");

const InvoiceSimulationSection = () => {
  // TRANSLATIONS
  const t = useTranslation("invoiceSimulation\\invoiceSimulation").t;

  // DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  const repository = useInjection<IRepository<CalculateResponseDTO>>(
    IocTypes.Repository
  );

  // STORE
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  const history = useHistory();

  // STATE
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>(
    {}
  );

  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(false);  

  // FORM
  const {
    values,
    setFormValue,
    errors,
    bindField,    
    valideteForm,    
    setValidationRule,
  } = useForm({
    initialValidations: newSimulationFormValidations(),
  });

  const handler = new InvoiceSimulationSectionHandler(
    setValidationRule,
    setFormValue,
    t
  );

  const redirectToSimulationDetail = (simulationId: number) => {
    history.push(`invoice-simulation/detail/${simulationId}`);
  };

  // QUERIES
  const getQueryParamsForContracts = (): URLSearchParams => {
    const result = new URLSearchParams();
    result.set("ContractStatus", ContractStatus.Effective);
    result.append("ContractType", ContractType.GasSmallConsumption);
    result.append("ContractType", ContractType.ElectricityLowVoltage);

    return result;
  };

  const contractsQuery: DataQuery = {
    url: Customer_ContractDropdown,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: getQueryParamsForContracts(),
  };
  const { data: contracts } = useQuery<ContractDropdownDTO[]>(
    contractsQuery,
    [],
    [customer]
  );

  const { data: lastKnownReading } = useQuery<LastKnownReadingDTO>(
    {
      url: Contract_Readings_LastKnown,
      routeParams: getRouteParams(
        "contractId",
        selectedContract?.contractId ?? ""
      ),
    },
    {},
    [selectedContract?.contractId],
    undefined,
    {
      apply: true,
      condition: () => selectedContract?.contractId !== undefined
    }
  );

  // EFFECTS
  useEffect(() => {
    if (Object.keys(lastKnownReading).length === 0) return; // Ještě nebyl nastaven

    const areReadingAndContractSet =
      lastKnownReading?.readingDate && selectedContract?.contractId;

    if (areReadingAndContractSet) {
      handler.setValidationByContractType(
        selectedContract.contractType!,
        lastKnownReading
      );
    } else {
      if (lastKnownReading?.previousContractId) {
        errorModalDialog(
          t("invoiceSimulationSection.noSelfReadingError.title"),
          t("invoiceSimulationSection.noPreviosContractSelfReadingError.message")
        );
      } else {
        errorModalDialog(
          t("invoiceSimulationSection.noSelfReadingError.title"),
          t("invoiceSimulationSection.noSelfReadingError.message")
        );
      }
    }

    setIsInputDisabled(!areReadingAndContractSet);
  }, [lastKnownReading?.readingDate]);

  // DIALOGS
  const errorModalDialog = (title: string, message: string) => {
    dialogService.openDialog(title, () => {
      return (
        <ContentDialogAlert
          alertObject={{
            alertType: "Danger",
            message: message,
          }}
        />
      );
    });
  };

  // HANDLERS
  const contractOnChangeHandler = async (
    contract: ContractDropdownDTO | null
  ) => {
    if (!contract) return;

    handler.clearFormValuesByContractType(contract?.contractType);

    setSelectedContract(contract);
  };

  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      if (!selectedContract?.contractId) return;

      let vtValue = "";
      if (selectedContract?.contractType === ContractType.GasSmallConsumption)
        vtValue = values["STAV"];

      if (selectedContract?.contractType === ContractType.ElectricityLowVoltage)
        vtValue = values["STAV_VT"];

      const queryParams = new URLSearchParams();
      queryParams.set("ContractId", selectedContract.contractId);
      queryParams.set("VTValue", vtValue);

      if (values["STAV_NT"]) {
        queryParams.set("NTValue", values["STAV_NT"]);
      }

      const calculateReponse = await repository.getOne({
        url: InvoiceSimulations_Calculate,
        params: queryParams,
      });

      const isCalculationSuccessful =
        calculateReponse.invoiceSimulationId &&
        calculateReponse.invoiceSimulationId > 0 &&
        !calculateReponse.errorMessage;

      if (isCalculationSuccessful) {
        redirectToSimulationDetail(calculateReponse.invoiceSimulationId ?? 0);
      } else {
        errorModalDialog(
          t("invoiceSimulationSection.simulationError.title"),
          calculateReponse.errorMessage ??
            t("invoiceSimulationSection.simulationError.message")
        );
      }
    }
  };

  // SUB-RENDERS
  const renderGasSmallConsumptionInputs = (): JSX.Element => {
    return (
      <div className="col-12 col-lg-6 px-2">
        <FormGroup
          labelText={t("invoiceSimulationSection.state")}
          validationError={errors ? errors["STAV"] : undefined}
        >
          <div className="input-group">
            <input
              disabled={isInputDisabled}
              className="form-control"
              placeholder={handler.getLastKnownReadingPlaceholder(
                "STAV",
                lastKnownReading,
                selectedContract?.contractType
              )}
              {...bindField("STAV")}
            />
            <div className="input-group-append">
              <span className="input-group-text">m<sup>3</sup></span>
            </div>
          </div>
        </FormGroup>
      </div>
    );
  };

  const renderElectricityLowVoltageInputs = (): JSX.Element => {
    return (
      <>
        <div className="col-12 col-lg-6 px-2">
          <FormGroup
            labelText={t("invoiceSimulationSection.stateVt")}
            validationError={errors ? errors["STAV_VT"] : undefined}
          >
            <div className="input-group">
              <input
                disabled={isInputDisabled}
                className="form-control"
                placeholder={handler.getLastKnownReadingPlaceholder(
                  "STAV_VT",
                  lastKnownReading,
                  selectedContract?.contractType
                )}
                {...bindField("STAV_VT")}
              />
              <div className="input-group-append">
                <span className="input-group-text">kWh</span>
              </div>
            </div>
          </FormGroup>
        </div>
        <div className="col-12 col-lg-6 px-2">
          <FormGroup
            labelText={t("invoiceSimulationSection.stateNt")}
            validationError={errors ? errors["STAV_NT"] : undefined}
          >
            <div className="input-group">
              <input
                disabled={isInputDisabled}
                className="form-control"
                placeholder={handler.getLastKnownReadingPlaceholder(
                  "STAV_NT",
                  lastKnownReading,
                  selectedContract?.contractType
                )}
                {...bindField("STAV_NT")}
              />
              <div className="input-group-append">
                <span className="input-group-text">kWh</span>
              </div>
            </div>
          </FormGroup>
        </div>
      </>
    );
  };

  const renderInputsByContractType = (
    contractType?: ContractType
  ): JSX.Element => {
    if (!contractType) return <></>;

    if (contractType === ContractType.ElectricityLowVoltage)
      return renderElectricityLowVoltageInputs();

    if (contractType === ContractType.GasSmallConsumption)
      return renderGasSmallConsumptionInputs();

    return <></>;
  };

  const renderAlert = () => { 
    let alerText = t("invoiceSimulationSection.alert");

    if (alerText !== "") {
      return (
        <Alert
          closable
          alertObj={
            {
              alertType: "Info",
              message: alerText
            }
          }
          className="mb-4"
        />
      );
    }
  }

  // RENDER
  const isSelectedContract = Object.keys(selectedContract).length > 0;
  return (
    <>
      {renderAlert()}
      <form onSubmit={submitFormHandler}>
        <PageCard title={t("invoiceSimulationSection.newSimulation")} bodyClassName="p-4">
          <div className="row no-gutters mx-n2 position-relative">
            <div className="col-12 col-lg-6 px-2">
              <ContractSelect
                name="DESC_TMSMLOUVA"
                contracts={contracts}
                onChange={contractOnChangeHandler}
                defaultContract={selectedContract}
              />
            </div>
            {isSelectedContract && (
              <div className="col-12 col-lg-6 px-2">
                <FormGroup labelText={t("invoiceSimulationSection.placeOfDelivery")}>
                  <input
                    className="form-control"
                    disabled
                    value={selectedContract.placeOfDelivery ?? ""}
                    readOnly
                  />
                </FormGroup>
              </div>
            )}
          </div>

          {isSelectedContract && (
            <div className="row no-gutters mx-n2 position-relative">
              {renderInputsByContractType(selectedContract.contractType)}
            </div>
          )}

          <button type="submit" className="btn btn-primary">
            {t("invoiceSimulationSection.sendSimulation")}
          </button>
        </PageCard>
      </form>

      {isSelectedContract && (
        <>
          <h5 className="mt-4 mb-3 text-muted">{t("invoiceSimulationSection.previousSimulation")}</h5>
          <DataGrid
            gridId={DataGridId.invoiceSimulationDetailGridId}
            url={Contract_InvoiceSimulations}
            filterJson={invoiceSimulationDataFilterJson}
            renderHeader={() => <InvoiceSimulationHeader />}
            renderItem={(item) => (
              <InvoiceSimulation invoiceSimulation={item} />
            )}
            routeParams={getRouteParams(
              "contractId",
              selectedContract?.contractId ?? ""
            )}
          />
        </>
      )}
    </>
  );
};

export default InvoiceSimulationSection;
